import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import {
  CameraRig,
  FALLBACK_MODEL_RADIUS,
  animateCameraToPose,
  animateCameraToQuaternion,
  aspectFor,
  fitCameraToObject,
  presetDirection,
  presetProjection,
  restoreCameraPose,
  sceneRadiusFor,
} from '../cameraRig';
import type { Projection, SnapToken, ViewPreset } from '../cameraRig';
import type { SharedCamera, SharedCameraSeat, SharedView } from '../sharedCamera';
import type { ModelMaterials } from '../modelLoader';
import type { ModelSize } from '../format';
import type { CapturedView, Environment, SelectionMode } from '../viewerSettings';
import {
  AmbientOcclusion,
  Backdrop,
  BuildPlatform,
  ContactShadow,
  DimensionGuides,
  MAX_CAST_TRIANGLES,
  MeasureTool,
  PartSelection,
  ScreenLabel,
  SectionGizmo,
  SectionView,
  ViewCube,
  ViewportGrid,
  ViewportLighting,
  ZoomPivot,
  modelColor,
  placementOffset,
  positionForOffset,
  sectionOffset,
  triangleCount,
  viewportBackground,
} from '../viewport';
import type {
  CubeFace,
  DimensionAxis,
  MeasureState,
  SectionAxis,
  SectionSettings,
  SelectedPart,
  UnderLightSettings,
} from '../viewport';
import { applyDisplayMode } from './displayModes';
import type { DisplayMode } from './displayModes';
import {
  disposeMaterials,
  disposeModel,
  disposeRenderable,
  parsedMaterials,
  saveOriginalMaterials,
} from './materials';
import type { MaterialSlot } from './materials';

// animateCameraToQuaternion sends the camera to where this axis points.
const SNAP_AXIS = new THREE.Vector3(0, 0, 1);

// What an empty workbench is framed on: the footprint of a typical part.
const EMPTY_WORKBENCH_HALF_SIZE = 50;

// The room map alone lights every face alike; dimmed, the headlight shapes the part.
const ENVIRONMENT_INTENSITY = 0.55;
// Sunk a hair below the floor so a part's underside, which lies on it, never
// z-fights the grid when the camera looks up from below.
const GRID_SINK = 1e-4;
// Occlusion is drawn only once the view has been still this long, so orbiting stays smooth.
const AO_SETTLE_MS = 140;
const ZOOM_IN_RATIO = 0.4;
const RENDER_JPEG_QUALITY = 0.9;

// Under the part's lowest point, so the shadow never draws over a face.
function shadowFloorFor(box: THREE.Box3): number {
  return Number.isFinite(box.min.z) ? Math.min(0, box.min.z) : 0;
}

export interface StageEvents {
  onContextLost: (lost: boolean) => void;
  onGridSpacing: (spacing: number) => void;
  onSelectedPart: (part: SelectedPart | null) => void;
  onMeasure: (state: MeasureState) => void;
  /** Whether the model on screen is light enough to pick and measure against. */
  onCastable: (castable: boolean) => void;
  onModelSize: (size: ModelSize) => void;
  onCubeHover: (over: boolean) => void;
  /** The section handles were dragged to these settings. */
  onSectionChange: (section: SectionSettings) => void;
  /** Where the cut now lies along its axis, in millimetres. */
  onSectionOffset: (offset: number) => void;
}

export interface StageSetup {
  container: HTMLDivElement;
  isDark: boolean;
  compact: boolean;
  cameraPose?: SharedCamera;
  displayMode: DisplayMode;
  paused: boolean;
  measuring: boolean;
  section: SectionSettings | null;
  environment: Environment;
  underLight: UnderLightSettings;
  gridSpacing: number | null;
  dimensions: boolean;
  cubeLabels?: Partial<Record<CubeFace, string>>;
  events: StageEvents;
}

/** The three.js side of one viewer: scene, camera, controls and render loop. */
export class ViewerStage {
  readonly materials: ModelMaterials;

  private readonly container: HTMLDivElement;
  private readonly isDark: boolean;
  private readonly compact: boolean;
  private readonly cameraPose?: SharedCamera;
  private readonly events: StageEvents;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly canvas: HTMLCanvasElement;
  private readonly scene = new THREE.Scene();
  private readonly lighting: ViewportLighting;
  private readonly rig: CameraRig;
  private readonly controls: OrbitControls;
  private readonly grid: ViewportGrid;
  private readonly shadow: ContactShadow;
  private readonly platform: BuildPlatform;
  private readonly dimensions: DimensionGuides;
  private readonly backdrop: Backdrop;
  private readonly cube: ViewCube | null;
  private readonly selection: PartSelection | null;
  private readonly measureTool: MeasureTool | null;
  private readonly sectionView: SectionView | null;
  private readonly gizmo: SectionGizmo | null;
  private readonly sectionBar = new ScreenLabel({ anchor: 'translate(14px, -50%)', clampPx: 8 });
  private readonly sectionAnchor = new THREE.Vector3();
  private readonly zoomPivot = new ZoomPivot();
  private readonly envTarget: THREE.WebGLRenderTarget;
  private readonly sharedMaterials: ReadonlySet<THREE.Material>;
  private readonly resizeObserver: ResizeObserver;
  private readonly originalMaterials = new Map<string, MaterialSlot>();
  // Everything a show has put in the scene, so one that threw part-way is
  // still cleared by the next.
  private readonly mounted = new Set<THREE.Object3D>();
  private readonly wheelPointer = new THREE.Vector2();
  private readonly viewAxis = new THREE.Vector3();
  private readonly offset = new THREE.Vector3();

  private displayMode: DisplayMode;
  private paused: boolean;
  private section: SectionSettings | null;
  private environment: Environment;
  private occlusion: AmbientOcclusion | null = null;
  private loaded: THREE.Object3D | null = null;
  // Where the file put the part, before Centre on origin and Sit on grid moved it.
  private fileBox: THREE.Box3 | null = null;
  // Kept so a reshaped panel can be refitted; holds no reference to the mesh.
  private modelBox: THREE.Box3 | null = null;
  private userMovedCamera = false;
  private seat: SharedCameraSeat | null = null;
  private activeSnap: SnapToken | null = null;
  private projectionLock: Projection | null = null;
  private animationId = 0;
  private disposed = false;
  private sceneRadius = FALLBACK_MODEL_RADIUS;
  private reportedSpacing: number | null = null;
  private viewportWidth: number;
  private viewportHeight: number;
  private framed = false;
  private castShadow: (() => void) | null = null;
  private lastChange = 0;
  private pointer: { x: number; y: number } | null = null;

  /** Null when the browser cannot give the viewer a WebGL context. */
  static create(setup: StageSetup): ViewerStage | null {
    let renderer: THREE.WebGLRenderer;
    try {
      // Stencil for the section caps.
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, stencil: true });
    } catch {
      console.error('[ModelViewer] WebGL context could not be created');
      return null;
    }
    return new ViewerStage(setup, renderer);
  }

  private constructor(setup: StageSetup, renderer: THREE.WebGLRenderer) {
    const { container, isDark, compact, cameraPose, events } = setup;
    this.container = container;
    this.isDark = isDark;
    this.compact = compact;
    this.cameraPose = cameraPose;
    this.events = events;
    this.displayMode = setup.displayMode;
    this.paused = setup.paused;
    this.section = setup.section;
    this.environment = setup.environment;
    this.renderer = renderer;
    this.viewportWidth = container.clientWidth;
    this.viewportHeight = container.clientHeight;

    const background = viewportBackground(isDark);
    this.scene.background = background;
    this.backdrop = new Backdrop(this.scene, background);
    this.lighting = new ViewportLighting(this.scene, isDark);
    this.rig = new CameraRig(aspectFor(this.viewportWidth, this.viewportHeight));

    const canvas = renderer.domElement;
    this.canvas = canvas;
    canvas.addEventListener('webglcontextlost', this.onContextLost);
    canvas.addEventListener('webglcontextrestored', this.onContextRestored);
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerleave', this.onPointerLeave);

    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(this.viewportWidth, this.viewportHeight);
    renderer.autoClear = false;
    renderer.localClippingEnabled = true;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.15;
    container.appendChild(canvas);

    this.grid = new ViewportGrid(isDark);
    this.scene.add(this.grid.mesh);
    this.shadow = new ContactShadow(isDark);
    this.scene.add(this.shadow.mesh);
    this.platform = new BuildPlatform(isDark);
    this.scene.add(this.platform.object);
    this.dimensions = new DimensionGuides(isDark);
    this.scene.add(this.dimensions.object);
    this.cube = compact
      ? null
      : new ViewCube(canvas, isDark, (direction) => this.snapTo(direction), {
          labels: setup.cubeLabels,
          onHover: events.onCubeHover,
        });
    const isOverCube = (event: PointerEvent) => this.cube?.contains(event.clientX, event.clientY) ?? false;
    const camera = () => this.rig.active;
    this.selection = compact
      ? null
      : new PartSelection(canvas, isDark, camera, isOverCube, events.onSelectedPart);
    if (this.selection) this.scene.add(this.selection.object);
    this.measureTool = compact ? null : new MeasureTool(canvas, isDark, camera, isOverCube, events.onMeasure);
    if (this.measureTool) this.scene.add(this.measureTool.object);
    this.selection?.setEnabled(!setup.measuring);
    this.measureTool?.setActive(setup.measuring);
    this.sectionView = compact ? null : new SectionView(isDark);
    if (this.sectionView) this.scene.add(this.sectionView.object);
    this.gizmo = compact
      ? null
      : new SectionGizmo(canvas, isDark, camera, isOverCube, events.onSectionChange, (dragging) => {
          this.controls.enabled = !dragging;
          if (dragging) this.selection?.clearHover();
        });
    if (this.gizmo) this.scene.add(this.gizmo.object);

    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();
    const roomEnvironment = new RoomEnvironment();
    this.envTarget = pmremGenerator.fromScene(roomEnvironment, 0.04);
    roomEnvironment.dispose();
    this.scene.environment = this.envTarget.texture;
    this.scene.environmentIntensity = ENVIRONMENT_INTENSITY;
    pmremGenerator.dispose();

    const controls = new OrbitControls(this.rig.active, renderer.domElement);
    this.controls = controls;
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.zoomToCursor = true;
    // Fires on wheel, pointer and touch, i.e. the user taking over the camera.
    controls.addEventListener('start', () => {
      this.userMovedCamera = true;
      this.seat?.lead();
    });
    // Zoom and pan keep the view direction, so a face view stays orthographic
    // through them; orbiting off the axis returns to perspective.
    controls.addEventListener('change', () => {
      this.lastChange = performance.now();
      this.selection?.invalidate();
      this.measureTool?.invalidate();
      if (
        !this.activeSnap &&
        this.projectionLock !== 'orthographic' &&
        this.rig.projection === 'orthographic' &&
        !this.rig.isAxisAlignedView(controls.target)
      ) {
        this.applyProjection('perspective');
      }
      this.seat?.publish(this.sharedView());
    });

    const defaultMaterial = new THREE.MeshStandardMaterial({
      color: modelColor(isDark),
      roughness: 0.5,
      metalness: 0.1,
    });
    const defaultLineMaterial = new THREE.LineBasicMaterial({ color: modelColor(isDark) });
    this.materials = { mesh: defaultMaterial, line: defaultLineMaterial };
    this.sharedMaterials = new Set([defaultMaterial, defaultLineMaterial]);

    container.addEventListener('wheel', this.onWheel, { capture: true, passive: true });

    this.seat =
      cameraPose?.join((view) => {
        if (this.activeSnap) this.activeSnap.cancelled = true;
        this.activeSnap = null;
        this.userMovedCamera = true;
        controls.minDistance = view.minDistance;
        controls.maxDistance = view.maxDistance;
        this.rig.applyView(view.pose, view.projection, controls);
      }) ?? null;

    this.applyStyle();
    this.setUnderLight(setup.environment.underLight ? setup.underLight : null);
    this.grid.setFixedSpacing(setup.gridSpacing);
    this.dimensions.show(setup.dimensions);

    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(container);
  }

  /** Frames the empty grid, as the bench a model will be put on. */
  showWorkbench(): void {
    const workbench = new THREE.Box3(
      new THREE.Vector3(-EMPTY_WORKBENCH_HALF_SIZE, -EMPTY_WORKBENCH_HALF_SIZE, 0),
      new THREE.Vector3(EMPTY_WORKBENCH_HALF_SIZE, EMPTY_WORKBENCH_HALF_SIZE, 0),
    );
    this.place(null, workbench);
    this.fit(workbench);
    this.draw();
  }

  // The section moves onto the new model before the old one is disposed:
  // its stencil meshes share the section's own materials.
  show(object: THREE.Object3D): void {
    this.mounted.add(object);
    const parsed = parsedMaterials(object);
    saveOriginalMaterials(object, this.originalMaterials);
    this.scene.add(object);
    this.fileBox = new THREE.Box3().setFromObject(object);
    placementOffset(this.fileBox, this.environment, this.offset);
    object.position.add(this.offset);
    object.updateMatrixWorld(true);
    const box = this.fileBox.clone().translate(this.offset);
    // Parsed materials can be shared between meshes, or be the viewer's
    // defaults, so they are freed once here rather than per mesh.
    applyDisplayMode(object, this.displayMode, this.isDark, this.originalMaterials, parsed);
    disposeMaterials([...parsed].filter((material) => !this.sharedMaterials.has(material)));
    this.attachSection(object, box);
    this.castShadowUnder(object, box);
    this.pointAt(object);

    for (const previous of this.mounted) {
      if (previous === object) continue;
      this.scene.remove(previous);
      disposeModel(previous, this.sharedMaterials, this.originalMaterials);
      this.mounted.delete(previous);
    }
    this.loaded = object;

    this.place(object, box);
    const { x, y, z } = box.getSize(new THREE.Vector3());
    this.events.onModelSize({ x, y, z });
    // Later models keep the camera where it is, so a page that rebuilds its
    // part on every step does not yank the view around.
    if (!this.framed) this.fit(box);
    this.framed = true;
    this.draw();
  }

  /** Frees a parsed model that never made it onto the scene. */
  discard(object: THREE.Object3D): void {
    disposeModel(object, this.sharedMaterials, this.originalMaterials);
  }

  setDisplayMode(mode: DisplayMode): void {
    this.displayMode = mode;
    this.applyStyle();
    const object = this.loaded;
    if (!object) return;
    applyDisplayMode(object, mode, this.isDark, this.originalMaterials);
    if (!this.sectionView) return;
    this.sectionView.set(this.section, mode !== 'wireframe');
    this.sectionView.applyTo(object);
  }

  setEnvironment(environment: Environment): void {
    const previous = this.environment;
    this.environment = environment;
    this.applyStyle();
    this.lastChange = performance.now();
    if (previous.centre !== environment.centre || previous.sit !== environment.sit) this.replace();
  }

  setUnderLight(settings: UnderLightSettings | null): void {
    this.lighting.setUnderLight(settings);
  }

  setGridSpacing(spacing: number | null): void {
    this.grid.setFixedSpacing(spacing);
  }

  setDimensions(shown: boolean): void {
    this.dimensions.show(shown);
  }

  attachDimensionLabel(axis: DimensionAxis, element: HTMLElement | null): void {
    this.dimensions.attachLabel(axis, element);
  }

  setBackdrop(image: ImageBitmap | null): void {
    this.backdrop.set(image, aspectFor(this.viewportWidth, this.viewportHeight));
  }

  setSection(section: SectionSettings | null): void {
    this.section = section;
    const { sectionView } = this;
    if (!sectionView) return;
    if (this.modelBox) this.gizmo?.set(section, this.modelBox);
    if (section) this.events.onSectionOffset(this.sectionOffsetOf(section));
    sectionView.set(section, this.displayMode !== 'wireframe');
    if (this.loaded) sectionView.applyTo(this.loaded);
    this.castShadow?.();
    const clip = section ? sectionView.plane : null;
    this.selection?.setClip(clip);
    this.measureTool?.setClip(clip);
    this.zoomPivot.setClip(clip);
    this.platform.setClip(clip);
  }

  attachSectionBar(element: HTMLElement | null): void {
    this.sectionBar.attach(element);
  }

  /** Where the cut lies along its axis on the part on screen, in millimetres. */
  sectionOffsetOf(section: SectionSettings): number {
    return this.modelBox ? sectionOffset(section, this.modelBox) : 0;
  }

  /** The 0–1 position along `axis` of a cut at `offset` millimetres. */
  sectionPositionFor(axis: SectionAxis, offset: number): number {
    return this.modelBox ? positionForOffset(axis, offset, this.modelBox) : 0.5;
  }

  setMeasuring(measuring: boolean): void {
    this.selection?.setEnabled(!measuring);
    this.measureTool?.setActive(measuring);
    if (measuring) this.selection?.clear();
  }

  /** Stops the render loop: a hidden viewer is still a WebGL context at 60fps. */
  setPaused(paused: boolean): void {
    this.paused = paused;
    if (paused) this.stop();
    else this.start();
  }

  setSelectionMode(mode: SelectionMode): void {
    this.selection?.setMode(mode);
  }

  /** False when there was nothing selected to clear. */
  clearSelection(): boolean {
    const had = this.selection?.hasSelection === true;
    this.selection?.clear();
    return had;
  }

  setMeasureLabel(label: HTMLDivElement | null): void {
    this.measureTool?.setLabel(label);
  }

  /** Flies back to the view that frames the whole model, in perspective. */
  home(): void {
    const { modelBox, rig, controls } = this;
    if (!modelBox) return;
    this.projectionLock = null;
    this.seat?.lead();
    if (this.activeSnap) this.activeSnap.cancelled = true;
    this.applyProjection('perspective');
    const from = rig.toPose(controls.target);
    fitCameraToObject(rig.perspective, modelBox, controls);
    const to = { position: rig.perspective.position.clone(), target: controls.target.clone() };
    restoreCameraPose(rig.perspective, controls, from);
    const token: SnapToken = { cancelled: false };
    this.activeSnap = token;
    animateCameraToPose(rig.perspective, controls, to, token, () => {
      this.activeSnap = null;
      this.userMovedCamera = false;
    });
  }

  setViewPreset(preset: ViewPreset): void {
    // A face view releases any projection held before, so it settles orthographic.
    this.projectionLock = presetProjection(preset);
    if (preset === 'perspective') this.applyProjection('perspective');
    this.snapTo(presetDirection(preset));
  }

  /** Holds the view in the other projection than the one on screen. */
  toggleProjection(): void {
    this.projectionLock = this.rig.projection === 'perspective' ? 'orthographic' : 'perspective';
    this.applyProjection(this.projectionLock);
  }

  /** Zooms in on what is under the pointer; false when the pointer is over nothing to land on. */
  zoomToPointer(): boolean {
    const pointer = this.pointer;
    if (!pointer) return false;
    const rect = this.canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;
    const ndc = new THREE.Vector2(
      ((pointer.x - rect.left) / rect.width) * 2 - 1,
      1 - ((pointer.y - rect.top) / rect.height) * 2,
    );
    const camera = this.rig.active;
    const depth = this.zoomPivot.depthAt(camera, ndc);
    if (depth === null) return false;
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc, camera);
    const point = ray.ray.at(depth, new THREE.Vector3());
    this.userMovedCamera = true;
    this.seat?.lead();
    if (this.activeSnap) this.activeSnap.cancelled = true;
    if (this.rig.projection === 'orthographic') {
      this.controls.target.copy(point);
      this.rig.orthographic.zoom /= ZOOM_IN_RATIO;
      this.rig.orthographic.updateProjectionMatrix();
      this.controls.update();
      return true;
    }
    const distance = Math.max(camera.position.distanceTo(point) * ZOOM_IN_RATIO, this.controls.minDistance);
    const position = camera.position.clone().sub(point).setLength(distance).add(point);
    const token: SnapToken = { cancelled: false };
    this.activeSnap = token;
    animateCameraToPose(camera, this.controls, { position, target: point }, token, () => {
      this.activeSnap = null;
    });
    return true;
  }

  // The context keeps no drawing buffer, so the frame has to be drawn and
  // read in one tick — anything in between leaves a blank image. Drawn
  // without the view cube or hover previews, which are chrome rather
  // than part of the model. A frame taken before the model arrives is an
  // empty scene, which is not something anyone can usefully mark.
  capture(): string | null {
    if (!this.loaded) return null;
    this.selection?.clearHover();
    this.measureTool?.clearHover();
    this.drawFrame();
    return this.canvas.toDataURL('image/png');
  }

  /** The part as the user sees it, without the floor, guides or tools, at most `maxEdge` px across. */
  captureView(maxEdge: number): CapturedView | null {
    if (!this.loaded) return null;
    const helpers: THREE.Object3D[] = [this.grid.mesh, this.platform.object, this.dimensions.object];
    if (this.selection) helpers.push(this.selection.object);
    if (this.measureTool) helpers.push(this.measureTool.object);
    if (this.gizmo) helpers.push(this.gizmo.object);
    const visible = helpers.map((object) => object.visible);
    helpers.forEach((object) => {
      object.visible = false;
    });
    this.selection?.clearHover();
    this.measureTool?.clearHover();
    try {
      this.drawFrame();
      const scale = Math.min(1, maxEdge / Math.max(this.canvas.width, this.canvas.height, 1));
      const out = document.createElement('canvas');
      out.width = Math.max(1, Math.round(this.canvas.width * scale));
      out.height = Math.max(1, Math.round(this.canvas.height * scale));
      const context = out.getContext('2d');
      if (!context) return null;
      context.drawImage(this.canvas, 0, 0, out.width, out.height);
      return {
        dataUrl: out.toDataURL('image/jpeg', RENDER_JPEG_QUALITY),
        width: out.width,
        height: out.height,
        background: this.backdrop.shown,
      };
    } finally {
      helpers.forEach((object, i) => {
        object.visible = visible[i];
      });
      if (this.paused) this.drawFrame();
    }
  }

  dispose(): void {
    this.disposed = true;
    this.seat?.leave();
    if (this.activeSnap) this.activeSnap.cancelled = true;
    cancelAnimationFrame(this.animationId);
    this.resizeObserver.disconnect();
    this.canvas.removeEventListener('webglcontextlost', this.onContextLost);
    this.canvas.removeEventListener('webglcontextrestored', this.onContextRestored);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerleave', this.onPointerLeave);
    this.controls.dispose();
    this.container.removeEventListener('wheel', this.onWheel, { capture: true });
    this.cube?.dispose();
    this.selection?.dispose();
    this.measureTool?.dispose();
    this.sectionView?.dispose();
    this.gizmo?.dispose();
    this.grid.dispose();
    this.shadow.dispose();
    this.platform.dispose();
    this.dimensions.dispose();
    this.backdrop.dispose();
    this.occlusion?.dispose();
    this.renderer.dispose();
    if (this.loaded && this.cameraPose) {
      this.cameraPose.current = this.rig.toPose(this.controls.target);
    }
    this.loaded = null;
    this.originalMaterials.forEach((material) => disposeMaterials(material));
    this.originalMaterials.clear();
    this.envTarget.dispose();
    this.scene.traverse(disposeRenderable);
    if (this.container.contains(this.canvas)) this.canvas.remove();
  }

  private readonly onContextLost = (event: Event) => {
    event.preventDefault();
    this.events.onContextLost(true);
  };

  private readonly onContextRestored = () => {
    this.events.onContextLost(false);
  };

  private readonly onPointerMove = (event: PointerEvent) => {
    this.pointer = { x: event.clientX, y: event.clientY };
  };

  private readonly onPointerLeave = () => {
    this.pointer = null;
  };

  // Zooming to the cursor steps by a share of the distance to the orbit
  // target, so the target is first moved to the depth of whatever is under
  // the cursor. Capture phase, to run before OrbitControls' own listener.
  private readonly onWheel = (event: WheelEvent) => {
    const rect = this.canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    this.wheelPointer.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      1 - ((event.clientY - rect.top) / rect.height) * 2,
    );
    const camera = this.rig.active;
    const depth = this.zoomPivot.depthAt(camera, this.wheelPointer);
    if (depth === null) return;
    camera.getWorldDirection(this.viewAxis);
    const { controls } = this;
    // OrbitControls clamps the camera-to-target distance to maxDistance before
    // it dollies, so a farther target would pull the camera in on a step out.
    const reach = Math.min(Math.max(depth, controls.minDistance), controls.maxDistance);
    controls.target.copy(camera.position).addScaledVector(this.viewAxis, reach);
  };

  private readonly onResize = () => {
    if (this.disposed) return;
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    this.viewportWidth = w;
    this.viewportHeight = h;
    this.rig.setAspect(aspectFor(w, h));
    this.renderer.setSize(w, h);
    this.occlusion?.setSize(w, h);
    this.backdrop.fit(aspectFor(w, h));
    // The fit distance depends on the aspect, so a reshaped panel needs a new
    // one or a narrowed viewport crops the model. Only while the camera is
    // still where the fit left it — never drag a view the user chose.
    if (this.modelBox && !this.userMovedCamera) {
      fitCameraToObject(this.rig.perspective, this.modelBox, this.controls);
    }
  };

  private readonly animate = () => {
    this.animationId = requestAnimationFrame(this.animate);
    this.controls.update();
    const camera = this.rig.active;
    this.selection?.update(camera);
    this.measureTool?.update(camera, this.viewportWidth, this.viewportHeight);
    this.dimensions.update(camera, this.viewportWidth, this.viewportHeight);
    this.gizmo?.update(camera);
    this.placeSectionBar(camera);
    this.drawFrame();
    this.cube?.render(this.renderer, camera);
    if (!this.compact) this.reportGridSpacing();
  };

  private placeSectionBar(camera: THREE.Camera): void {
    const anchor = this.section && this.sectionView ? this.sectionView.anchor(this.sectionAnchor) : null;
    this.sectionBar.place(anchor, camera, this.viewportWidth, this.viewportHeight);
  }

  private start(): void {
    if (!this.animationId && !this.disposed) this.animate();
  }

  private stop(): void {
    cancelAnimationFrame(this.animationId);
    this.animationId = 0;
  }

  private draw(): void {
    if (this.paused) this.drawFrame();
    else this.start();
  }

  private drawFrame(): void {
    const { rig, controls, renderer, scene } = this;
    const camera = rig.active;
    this.grid.update(camera, controls.target);
    this.lighting.follow(camera, controls.target);
    this.shadow.update(camera);
    rig.applyClipPlanes(controls, Math.max(this.sceneRadius, this.grid.radius));
    renderer.clear();
    renderer.render(scene, camera);
    const occlusion = this.occlusionFor();
    if (occlusion && this.loaded && this.modelBox) {
      occlusion.render(camera, this.loaded, this.modelBox, this.section ? this.sectionView?.plane ?? null : null);
    }
  }

  // Built on first use: most viewers never switch it on, and its buffers are large.
  private occlusionFor(): AmbientOcclusion | null {
    const wanted =
      this.environment.occlusion &&
      !this.compact &&
      this.loaded !== null &&
      (this.displayMode === 'solid-edges' || this.displayMode === 'shaded');
    if (!wanted || performance.now() - this.lastChange < AO_SETTLE_MS) return null;
    this.occlusion ??= new AmbientOcclusion(this.renderer, this.scene, this.rig.active);
    return this.occlusion;
  }

  private applyStyle(): void {
    const mode = this.displayMode;
    const { shadows, grid, platform } = this.environment;
    this.shadow.show(shadows && mode !== 'wireframe');
    this.grid.show(grid);
    this.platform.show(platform && !this.compact);
    this.platform.setStyle({
      faces: mode !== 'wireframe',
      rim: mode !== 'shaded',
      ghosted: mode === 'ghosted',
    });
    this.grid.setHole(this.platform.cover);
  }

  // Centre on origin and Sit on grid moved: the part goes where they now put it.
  private replace(): void {
    const object = this.loaded;
    const fileBox = this.fileBox;
    if (!object || !fileBox) return;
    object.position.sub(this.offset);
    placementOffset(fileBox, this.environment, this.offset);
    object.position.add(this.offset);
    object.updateMatrixWorld(true);
    const box = fileBox.clone().translate(this.offset);
    this.attachSection(object, box);
    this.castShadowUnder(object, box);
    this.pointAt(object);
    this.place(object, box);
    this.home();
  }

  private attachSection(object: THREE.Object3D, box: THREE.Box3): void {
    const { sectionView } = this;
    if (!sectionView) return;
    sectionView.attach(object, box);
    sectionView.set(this.section, this.displayMode !== 'wireframe');
    sectionView.applyTo(object);
    this.gizmo?.set(this.section, box);
    if (this.section) this.events.onSectionOffset(sectionOffset(this.section, box));
  }

  private castShadowUnder(object: THREE.Object3D, box: THREE.Box3): void {
    const { sectionView } = this;
    this.castShadow = () =>
      this.shadow.render(
        this.renderer,
        object,
        box,
        shadowFloorFor(box),
        this.section && sectionView ? sectionView.plane : null,
      );
    this.castShadow();
  }

  private reportGridSpacing(): void {
    const spacing = this.grid.spacingAt(this.rig.active, this.controls.target, this.viewportHeight);
    if (spacing === this.reportedSpacing) return;
    this.reportedSpacing = spacing;
    this.events.onGridSpacing(spacing);
  }

  private sharedView(): SharedView {
    return {
      pose: this.rig.toPose(this.controls.target),
      projection: this.rig.projection,
      minDistance: this.controls.minDistance,
      maxDistance: this.controls.maxDistance,
    };
  }

  // Published here as well as on change: a switch that keeps the camera
  // where it is fires no change event, and followers would stay behind in
  // the other projection.
  private applyProjection(next: Projection): void {
    this.rig.setProjection(next, this.controls);
    this.seat?.publish(this.sharedView());
  }

  private settleProjection(): void {
    this.applyProjection(this.projectionLock ?? this.rig.projectionForView(this.controls.target));
  }

  private snapTo(direction: THREE.Vector3): void {
    this.userMovedCamera = true;
    this.seat?.lead();
    if (this.activeSnap) this.activeSnap.cancelled = true;
    const token: SnapToken = { cancelled: false };
    this.activeSnap = token;
    const toward = new THREE.Quaternion().setFromUnitVectors(SNAP_AXIS, direction);
    animateCameraToQuaternion(this.rig.active, this.controls, toward, token, () => {
      this.activeSnap = null;
      this.settleProjection();
    });
  }

  private place(object: THREE.Object3D | null, box: THREE.Box3): void {
    const size = box.getSize(new THREE.Vector3());
    const footprint = Math.max(size.x, size.y, 1);
    const gridSize = footprint * 4;
    // On the CAD origin plane, as in the authoring tool, even through a part reaching below it.
    const gridZ = -footprint * GRID_SINK;
    this.grid.place(gridZ, gridSize / 2);
    this.zoomPivot.setScene(object, box, 0);
    this.sceneRadius = sceneRadiusFor(box, gridSize, gridZ);
    this.modelBox = box;
    this.platform.place(object ? box : null);
    this.dimensions.set(object ? box : null);
    this.grid.setHole(this.platform.cover);
  }

  private fit(box: THREE.Box3): void {
    const saved = this.cameraPose?.current;
    fitCameraToObject(this.rig.perspective, box, this.controls);
    if (saved) {
      restoreCameraPose(this.rig.perspective, this.controls, saved);
      this.settleProjection();
      this.userMovedCamera = true;
    }
  }

  private pointAt(object: THREE.Object3D): void {
    const castable = triangleCount(object) <= MAX_CAST_TRIANGLES ? object : null;
    this.selection?.setModel(castable);
    this.measureTool?.setModel(castable);
    this.events.onCastable(castable !== null);
    const clip = this.section && this.sectionView ? this.sectionView.plane : null;
    this.selection?.setClip(clip);
    this.measureTool?.setClip(clip);
    this.zoomPivot.setClip(clip);
    this.platform.setClip(clip);
  }
}
